import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/site/ProductCard";
import LaunchPromoBanner from "@/components/site/LaunchPromoBanner";
import WelcomeOfferBanner from "@/components/site/WelcomeOfferBanner";
import { useGoogleReviews } from "@/context/GoogleReviewsContext";
import { Stars } from "@/components/site/Stars";
import { Star } from "lucide-react";
import { cldImage } from "@/lib/cldImage";

const GOOGLE_REVIEW_URL = "https://g.page/r/CdeH-owIuo6vEAI/review";

const HERO_IMG  = cldImage("https://images.unsplash.com/photo-1571877227200-a0d98ea607e9", { w: 1600 });
const TUB_IMG   = cldImage("https://images.unsplash.com/photo-1568627175730-73d05bd69ca9", { w: 1400 });
const SHAKE_IMG = cldImage("https://customer-assets-4nw71qhi.emergentagent.net/job_premium-tiramisu-1/artifacts/ed9ov0sg_ChatGPT%20Image%20Sep%209%2C%202026%2C%2002_40_20%20PM.png", { w: 1400 });

const values = [
  { t: "Crafted small-batch", d: "Every cup layered by hand, every day. Never rushed, never mass-produced." },
  { t: "Uncompromising ingredients", d: "Freshly prepared at home, single-origin espresso, real cocoa. No shortcuts." },
  { t: "Fresh, always", d: "Made-to-order for delivery windows so it reaches you at its best." },
  { t: "Boutique packaging", d: "Considered, minimalist packaging that arrives as gently as it was made." },
];

const faqs = [
  { q: "Where do you deliver?", a: "We currently deliver across select pincodes in Ghaziabad, with pickup available from our kitchen. Delhi NCR is on the horizon." },
  { q: "How is it packaged?", a: "Every order arrives in a kraft carry bag with a thank-you card, wooden spoons, tissue and — for direct orders — a complimentary cookie." },
  { q: "Is it eggless?", a: "Our Traditional Italian uses egg yolk in the classical style. Please check individual product labels — anything eggless is clearly marked." },
  { q: "How fresh is it?", a: "Made the same day for your delivery slot. We recommend enjoying within 24 hours." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { data: googleReviews } = useGoogleReviews();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const cups = products.filter((p) => p.category === "cups");
  const shakes = products.filter((p) => p.category === "shakes");
  const tubs = products.filter((p) => p.category === "tubs");
  const isSoldOut = (product) => (
    ["cups", "tubs"].includes(product.category)
    && typeof product.stock_qty === "number"
    && product.stock_qty <= 0
  );

  return (
    <main data-testid="home-page">
      <WelcomeOfferBanner />
      <LaunchPromoBanner />
      {/* HERO */}
      <section className="relative">
        <div className="mx-auto grid max-w-[1400px] items-center gap-7 px-4 pt-7 md:grid-cols-12 md:gap-10 md:px-10 md:pt-16">
          <div className="order-1 md:col-span-6 md:order-1">
            <div className="label-xs text-espresso-light">Ghaziabad · Est. 2026</div>
            <h1 className="mt-4 font-serif text-4xl font-normal leading-[0.95] tracking-tight text-espresso md:mt-6 md:text-6xl lg:text-[5.5rem]">
              A specialist,<br/>
              <span className="italic text-espresso-light font-light">not a bakery.</span>
            </h1>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-espresso-light md:mt-8 md:text-base">
              TIRAMISÙ &amp; Co. is a boutique brand devoted to a single, obsessively-crafted dessert — tiramisu.
              Layered with freshly prepared mascarpone made at home, single-origin espresso and real cocoa.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-3 md:mt-10 md:flex md:flex-wrap md:gap-4">
              <Link to="/menu" className="btn-espresso px-3 text-center md:px-7" data-testid="hero-order-btn">Order now</Link>
              <Link to="/menu" className="btn-outline px-3 text-center md:px-7" data-testid="hero-menu-btn">Explore menu</Link>
            </div>
            <p className="mt-7 font-serif text-base italic text-espresso-light md:mt-10 md:text-lg">
              "One Spoon Closer to Happiness."
            </p>
          </div>
          <div className="order-2 md:col-span-6 md:order-2">
            <div className="relative aspect-[5/4] max-h-[720px] overflow-hidden bg-ivory-100 md:aspect-[4/5]">
              <img src={HERO_IMG} alt="Tiramisu with mascarpone and cocoa" className="w-full h-full object-cover" />
              <div className="absolute bottom-6 left-6 bg-ivory/95 backdrop-blur px-5 py-3">
                <div className="label-xs text-espresso-light">Signature</div>
                <div className="font-serif text-xl text-espresso">Traditional Italian</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="referral-home-promo" className="mt-16 border-y border-line bg-ivory-100 md:mt-20">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-4 py-8 md:flex-row md:items-end md:justify-between md:px-10 md:py-10">
          <div>
            <div className="label-xs text-espresso-light">A sweeter kind of sharing</div>
            <h2 className="mt-3 font-serif text-3xl text-espresso md:text-4xl">
              Share the love — give friends 10% off, get a free cup for every 3 who order.
            </h2>
            <p data-testid="direct-order-saving-copy" className="mt-4 text-sm text-espresso-light">
              Order direct and save — our prices here are lower than on delivery apps.
            </p>
          </div>
          <Link to="/refer-a-friend" data-testid="home-refer-a-friend-link" className="btn-outline shrink-0">
            Refer a friend
          </Link>
        </div>
      </section>

      {/* SIGNATURE STRIP */}
      <section className="mt-20 md:mt-32">
        <div className="mx-auto max-w-[1400px] px-4 md:px-10">
          <div className="label-xs text-espresso-light">Tiramisù, your way</div>
          <h2 className="mt-4 font-serif text-3xl font-normal leading-[0.98] text-espresso md:text-6xl">
            Whether it's just you,<br/>
            <span className="italic text-espresso-light">the two of you, or a table full of friends.</span>
          </h2>
          <div className="mt-9 grid grid-cols-2 gap-3 md:mt-14 md:grid-cols-2 md:gap-6 lg:grid-cols-4">
            {[
              { size: 1, tag: "Just for Me",       sub: "Single Cup", desc: "For your personal tiramisù moment.",           discount: 0 },
              { size: 2, tag: "For Two",           sub: "2 Cups",     desc: "Two cups. One dessert moment.",                discount: 7 },
              { size: 4, tag: "To Share",          sub: "4 Cups",     desc: "Made for sharing with family and friends.",    discount: 12 },
              { size: 6, tag: "For the Occasion",  sub: "6 Cups",     desc: "For celebrations, gatherings and gifting.",    discount: 15 },
            ].map((b) => (
              <Link key={b.size} to={`/box/${b.size}`} data-testid={`box-card-${b.size}`}
                className="group relative overflow-hidden border border-line p-5 transition-colors duration-200 hover:border-espresso md:p-8">
                {b.discount > 0 && (
                  <div
                    data-testid={`box-card-discount-${b.size}`}
                    className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold text-espresso text-[10px] tracking-[0.18em] font-semibold uppercase"
                  >
                    {b.discount}% off
                  </div>
                )}
                <div className="label-xs text-gold">{b.sub}</div>
                <div className="mt-3 font-serif text-2xl font-bold text-espresso md:mt-4 md:text-3xl">{b.tag}</div>
                <p className="mt-3 hidden min-h-[60px] text-sm leading-relaxed text-espresso-light sm:block">{b.desc}</p>
                <div className="mt-5 label-xs link-underline text-espresso md:mt-6">Choose →</div>
              </Link>
            ))}
          </div>
          <div className="mt-14 border-t border-line pt-8 text-center">
            <p className="text-espresso-light">Planning something bigger?</p>
            <Link to="/bulk" className="mt-2 inline-block label-xs link-underline text-espresso" data-testid="bulk-cta">
              From office treats to celebrations, explore Bulk Orders →
            </Link>
          </div>
        </div>
      </section>

      {/* SIGNATURE FLAVOURS */}
      <section className="mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end border-b border-line pb-16">
          <div className="md:col-span-5">
            <div className="label-xs text-espresso-light">The Signature</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight text-espresso">
              Four flavours.<br/>One obsession.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-espresso-light leading-relaxed">
            From our Traditional Italian — the everyday hero — to Biscoff, Pistachio and Nutella,
            each flavour is developed to stand alone, and to be reordered.
          </p>
        </div>
      </section>

      {/* CUPS */}
      <section className="mt-16 md:mt-28">
        <div className="mx-auto max-w-[1400px] px-4 md:px-10">
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-2 md:gap-x-8 md:gap-y-16 lg:grid-cols-3">
            {cups.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* SHAKES */}
      <section className="mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="aspect-[4/3] max-h-[360px] overflow-hidden bg-ivory-100">
              <img src={SHAKE_IMG} alt="Tiramisu shake" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex flex-col justify-center">
            <div className="label-xs text-espresso-light">Tiramisù, blended</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-espresso leading-tight">
              Shakes,<br/>the same restraint.
            </h2>
            <p className="mt-6 text-espresso-light leading-relaxed max-w-md">
              Cold, creamy, and unmistakably tiramisù. Four flavours — Classic, Biscoff, Pistachio, Nutella.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              {shakes.slice(0, 4).map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.slug}`}
                  data-testid={`home-product-link-${p.slug}`}
                  className="group border-b border-line pb-4"
                >
                  <div className="flex justify-between items-baseline">
                    <span className="font-serif text-xl text-espresso group-hover:text-gold-hover transition-colors">
                      {p.name.replace(" Shake", "")}
                    </span>
                    <span className="font-serif text-lg text-espresso">₹{p.price}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAMILY TUBS */}
      <section className="mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="aspect-[4/3] overflow-hidden bg-ivory-100 max-h-[420px]">
              <img src={TUB_IMG} alt="Family tubs" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="label-xs text-espresso-light">Family Tubs</div>
            <h3 className="mt-3 font-serif text-4xl md:text-5xl text-espresso leading-tight">The centrepiece.</h3>
            <p className="mt-4 text-espresso-light max-w-md leading-relaxed">
              Our whole recipe, in a shareable tub. Enough for four to six.
            </p>
            <div className="mt-8 space-y-3">
              {tubs.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.slug}`}
                  data-testid={`home-product-link-${p.slug}`}
                  className="flex justify-between items-baseline gap-3 border-b border-line pb-3 hover:text-gold-hover transition-colors"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="font-serif text-xl text-espresso">{p.name}</span>
                    {isSoldOut(p) && (
                      <span
                        data-testid={`home-sold-out-${p.slug}`}
                        className="shrink-0 border border-rose-700 bg-rose-50 px-2 py-1 text-[10px] font-semibold tracking-[0.12em] text-rose-800"
                      >
                        SOLD OUT
                      </span>
                    )}
                  </span>
                  <span className="font-serif text-lg text-espresso">₹{p.price}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="mt-32 bg-ivory-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="label-xs text-espresso-light">Why TIRAMISÙ &amp; Co.</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-espresso leading-tight max-w-2xl">
            Restraint, not shortcuts.
          </h2>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {values.map((v, i) => (
              <div key={v.t}>
                <div className="font-serif text-3xl text-gold">0{i + 1}</div>
                <div className="font-serif text-2xl text-espresso mt-3">{v.t}</div>
                <p className="mt-3 text-espresso-light text-sm leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOVED BY GOOGLE — surfaces as soon as we have ONE real Google review */}
      {(googleReviews?.reviews?.length || 0) > 0 && (
        <section className="mt-32" data-testid="home-google-reviews">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid md:grid-cols-12 gap-10 items-end border-b border-line pb-12">
              <div className="md:col-span-7">
                <div className="label-xs text-espresso-light inline-flex items-center gap-2">
                  <Star size={12} strokeWidth={1.5} className="fill-gold text-gold" />
                  Real reviews · straight from Google
                </div>
                <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[0.95] text-espresso">
                  Loved by tiramisù<br/><span className="italic text-espresso-light">people.</span>
                </h2>
                {googleReviews?.rating != null && googleReviews?.total > 0 && (
                  <div className="mt-6 flex items-baseline gap-4 flex-wrap" data-testid="home-google-rating">
                    <Stars value={googleReviews.rating} size={22} showValue />
                    <span className="text-espresso-light text-sm">
                      Based on {googleReviews.total} Google review{googleReviews.total === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
              </div>
              <div className="md:col-span-4 md:col-start-9 flex flex-col gap-3">
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="home-write-google-review"
                  className="btn-espresso self-start"
                >
                  Write a Google review
                </a>
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="label-xs link-underline text-espresso"
                  data-testid="home-read-google-reviews"
                >
                  Read all on Google →
                </a>
              </div>
            </div>
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {googleReviews.reviews.slice(0, 6).map((r, i) => (
                <article
                  key={r.time || i}
                  data-testid="home-google-review-card"
                  className="border border-line p-6 md:p-8 bg-ivory h-full flex flex-col"
                >
                  <div className="flex items-center gap-3">
                    {r.profile_photo_url ? (
                      <img
                        src={r.profile_photo_url}
                        alt={r.author_name}
                        className="w-10 h-10 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-ivory-100 flex items-center justify-center text-espresso font-serif">
                        {(r.author_name || "?").slice(0, 1)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-serif text-lg text-espresso truncate" data-testid="review-author">
                        {r.author_name || "Google reviewer"}
                      </div>
                      <div className="label-xs text-espresso-light">
                        {r.relative_time_description}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Stars value={r.rating || 0} size={14} />
                  </div>
                  {r.text && (
                    <p
                      className="mt-4 font-serif italic text-espresso leading-snug text-lg md:text-xl whitespace-pre-line line-clamp-6"
                      data-testid="review-text"
                    >
                      "{r.text}"
                    </p>
                  )}
                  {Array.isArray(r.photos) && r.photos.length > 0 && (
                    <div
                      className={`mt-4 grid gap-2 ${r.photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
                      data-testid="review-photos"
                    >
                      {r.photos.slice(0, 4).map((src, idx) => (
                        <a
                          key={idx}
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          className="block aspect-square overflow-hidden bg-ivory-100"
                        >
                          <img
                            src={src}
                            alt={`Photo by ${r.author_name || "Google reviewer"}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
            {Array.isArray(googleReviews?.photos) && googleReviews.photos.length > 0 && (
              <div className="mt-16 border-t border-line pt-12" data-testid="home-google-photos">
                <div className="grid md:grid-cols-12 gap-10 items-end pb-8">
                  <div className="md:col-span-7">
                    <div className="label-xs text-espresso-light">Real proof</div>
                    <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-tight text-espresso">
                      Photos from our Google reviewers.
                    </h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  {googleReviews.photos.slice(0, 8).map((src, idx) => (
                    <a
                      key={idx}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`home-google-photo-${idx}`}
                      className="block aspect-square overflow-hidden bg-ivory-100 group"
                    >
                      <img
                        src={src}
                        alt={`Google reviewer photo ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* DELIVERY / PICKUP */}
      <section className="mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10">
          <div className="border border-line p-10">
            <div className="label-xs text-espresso-light">Delivery</div>
            <h3 className="font-serif text-3xl text-espresso mt-3">To your door in Ghaziabad.</h3>
            <p className="mt-4 text-espresso-light">Select pincodes across Ghaziabad. Enter yours at checkout to confirm.</p>
            <Link to="/menu" className="mt-6 inline-block label-xs link-underline text-espresso">Order for delivery</Link>
          </div>
          <div className="border border-line p-10">
            <div className="label-xs text-espresso-light">Pickup</div>
            <h3 className="font-serif text-3xl text-espresso mt-3">Collect from our kitchen.</h3>
            <p className="mt-4 text-espresso-light">Choose a pickup slot at checkout — no delivery fee, and often a complimentary cookie.</p>
            <Link to="/menu" className="mt-6 inline-block label-xs link-underline text-espresso">Order for pickup</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="label-xs text-espresso-light">Small print,<br/>gently addressed</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-espresso leading-tight">FAQ.</h2>
          </div>
          <div className="md:col-span-8 space-y-8">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-line pb-6">
                <div className="font-serif text-xl text-espresso">{f.q}</div>
                <p className="mt-2 text-espresso-light leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-32 bg-espresso text-ivory">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            One spoon. <span className="italic text-gold">Closer.</span>
          </h2>
          <p className="mt-8 text-ivory/70 max-w-md mx-auto">Order online. Delivered or picked up. Freshly layered.</p>
          <Link to="/menu" className="mt-10 inline-block bg-ivory text-espresso px-10 py-4 rounded-full label-xs" data-testid="final-cta">
            Order now
          </Link>
        </div>
      </section>
    </main>
  );
}
